import React, { Suspense, lazy } from 'react'

import { ThemeProvider } from '@mui/material'
import $ from 'jquery'
import has from 'lodash/has'
import includes from 'lodash/includes'
import { transform as OLProjTransform } from 'ol/proj'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'

import { HistoryInit } from 'api/history'
import * as Model from 'api/model'
import * as PoziUtils from 'api/utils'
import { hideHotJarIfMobilePortrait } from 'app/hotjar'
import PoziStyle from 'app/style'
import AuthAWS from 'aws/auth'
import PoziConfig from 'config/config'
import PoziDataRetrieval from 'data/retrieval'
import AboutPanel from 'drawer/tabs/about/AboutPanel'
import DrawerAboutIcon from 'drawer/tabs/about/DrawerAboutIcon'
import PoziAddFeature from 'drawer/tabs/info/add-feature'
import PoziEditFeature from 'drawer/tabs/info/edit-feature'
import PoziFragments from 'drawer/tabs/info/fragment/fragments'
import PoziGeoServer from 'drawer/tabs/info/geoserver'
import PoziDrawerTabInfo from 'drawer/tabs/info/info'
import Layers from 'drawer/tabs/layers/Layers'
import SearchPanels from 'drawer/tabs/search/panels/SearchPanels'
import PoziDrawerTabSearch from 'drawer/tabs/search/Search'
import ToolsPanels from 'drawer/tabs/tools/panels/ToolsPanels'
import { redirectToSharedLink } from 'drawer/tabs/tools/share'
import { resetCurrentTarget } from 'map/actions'
import PoziMap from 'map/map'
import PoziMapBase from 'map/map-base'
import { poziTheme } from 'theme/theme'
import PoziHelpers from 'utils/helpers'
import MapContextMenu from 'utils/MapContextMenu'
import PoziNotifications from 'utils/notifications'
import PoziPingdom from 'utils/pingdom'
import PrintAdvanced from 'utils/Print/PrintAdvanced'

// Lazy load some components. This also triggers code splitting
const ConfigManager = lazy(() => import('ConfigManager/ConfigManager'))

const SiteList = lazy(() => import('SiteList/SiteList'))
const PositionedSnackbar = lazy(() => import('utils/snackbar/PositionedSnackbar'))

const InfoPanels = lazy(() => import('drawer/tabs/info/panels/InfoPanels'))

const LayerTable = lazy(() => import('drawer/tabs/layers/table/LayerTable'))

// The following components are too small to split
// const PrintAdvanced = lazy(() => import('utils/Print/PrintAdvanced'))
// const ToolsPanels = lazy(() => import('drawer/tabs/tools/panels/ToolsPanels'))

// Empty loading fallback
const Loading = () => <span />

// Stopping / hiding the spinner
export const hideLoadingScreen = function (transitionTime = 1000) {
  const loadingTextEl = document.getElementById('loadingText')
  loadingTextEl.innerText = 'Ready.'
  $('#loadingScreen,#loadingSpinner').fadeOut(transitionTime)
}

export const progressBarIncreaseMax = (increaseBy = 1) => {
  const progressBarEl = document.getElementById('loadingProgressBar')
  if (!progressBarEl) {
    return
  }
  const currMax = Number(progressBarEl.getAttribute('max'))
  const newMax = String(currMax + increaseBy)

  progressBarEl.setAttribute('max', newMax)

  // console.debug('Progress - increase max from/to', currMax, newMax)
}

export const progressBarIncreaseValue = (increaseBy = 1) => {
  const progressBarEl = document.getElementById('loadingProgressBar')
  if (!progressBarEl) {
    return
  }
  const currValue = Number(progressBarEl.getAttribute('value'))
  const newValue = String(currValue + increaseBy)

  progressBarEl.setAttribute('value', newValue)

  // console.debug('Progress - increase value from/to', currValue, newValue)
}

/**
 * Pozi App module.
 * Entry point for the application.
 */
const PoziApp = function () {
  // ============================================================
  // Public Variables
  // TODO: Rework required to make most of these private.
  // ============================================================

  /**
   * This object
   * @type {object}
   */
  const __ = this
  __.blank = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII='

  __.poziConfig = {}
  __.layerConfig = {}
  __.featureCache = {}
  __.routing = {}
  __.loadscripts = {}
  __.poziMap = {}
  __.pingdom = {}
  __.spinner = {}

  // Some versions
  __.buildInfo = POZI_BUILD_INFO

  // ============================================================
  // Private Variables
  // ============================================================

  // ============================================================
  // Public Functions
  // ============================================================

  __.start = function () {
    console.debug('Pozi app start...')

    console.debug('Build info (version, git info, etc)', this.buildInfo)

    // Mainly for people with a slow connection
    $('#loadingText').html('Loading...')

    Model.initialise()
      .then(async function () {
        const sitename = Model.get('app.site', '')
        if (sitename === 'go') {
          console.log('Detected redirector domain... redirecting to short URL if possible.')
          redirectToSharedLink()

          return
        }

        // Remove all of #widget-wrapper as we don't need it and it has a duplicate #q input
        $('#widget-wrapper').remove()

        const sitelist = Model.getState('sitelist', false)
        if (sitelist) {
          console.log('Creating sitelist!')
          createRoot(document.getElementById('react-sitelist')).render(
            <Provider store={window.store}>
              <Suspense fallback={<Loading />}>
                <ThemeProvider theme={poziTheme}>
                  <SiteList />
                </ThemeProvider>
              </Suspense>
            </Provider>
          )

          hideLoadingScreen()

          return
        }

        showApp()

        // addIntercom()

        const snackbarEl = createRoot(document.getElementById('react-positioned-snackbar'))
        snackbarEl.render(
          <Provider store={window.store}>
            <Suspense fallback={<Loading />}>
              <ThemeProvider theme={poziTheme}>
                <PositionedSnackbar />
              </ThemeProvider>
            </Suspense>
          </Provider>
        )

        console.debug('Positioned snackbar createRoot', snackbarEl)

        createRoot(document.getElementById('react-print-advanced')).render(
          <Provider store={window.store}>
            <ThemeProvider theme={poziTheme}>
              <PrintAdvanced />
            </ThemeProvider>
          </Provider>
        )

        const renderConfigManager = Model.getState('config', false)
        if (renderConfigManager) {
          createRoot(document.getElementById('react-config-manager')).render(
            <Provider store={window.store}>
              <Suspense fallback={<Loading />}>
                <ThemeProvider theme={poziTheme}>
                  <ConfigManager />
                </ThemeProvider>
              </Suspense>
            </Provider>
          )
        }

        createRoot(document.getElementById('pozi-drawer-tabs-panel-info-react')).render(
          <Provider store={window.store}>
            <Suspense fallback={<Loading />}>
              <ThemeProvider theme={poziTheme}>
                <InfoPanels />
              </ThemeProvider>
            </Suspense>
          </Provider>
        )

        createRoot(document.getElementById('pozi-drawer-tabs-panel-layers-react')).render(
          <Provider store={window.store}>
            <Suspense fallback={<Loading />}>
              <ThemeProvider theme={poziTheme}>
                <Layers />
              </ThemeProvider>
            </Suspense>
          </Provider>
        )

        createRoot(document.getElementById('pozi-drawer-tabs-panel-tools-react')).render(
          <Provider store={window.store}>
            <Suspense fallback={<Loading />}>
              <ThemeProvider theme={poziTheme}>
                <ToolsPanels />
              </ThemeProvider>
            </Suspense>
          </Provider>
        )

        createRoot(document.getElementById('pozi-drawer-tabs-panel-about-react')).render(
          <Provider store={window.store}>
            <Suspense fallback={<Loading />}>
              <ThemeProvider theme={poziTheme}>
                <AboutPanel />
              </ThemeProvider>
            </Suspense>
          </Provider>
        )

        createRoot(document.getElementById('pozi-drawer-tabs-header-icon-react')).render(
          <Provider store={window.store}>
            <ThemeProvider theme={poziTheme}>
              <DrawerAboutIcon />
            </ThemeProvider>
          </Provider>
        )

        createRoot(document.getElementById('layerTableData')).render(
          <Provider store={window.store}>
            <Suspense fallback={<Loading />}>
              <ThemeProvider theme={poziTheme}>
                <LayerTable />
              </ThemeProvider>
            </Suspense>
          </Provider>
        )

        createRoot(document.getElementById('map-context-menu')).render(
          <Provider store={window.store}>
            <Suspense fallback={<Loading />}>
              <ThemeProvider theme={poziTheme}>
                <MapContextMenu />
              </ThemeProvider>
            </Suspense>
          </Provider>
        )

        PoziStyle().apply()

        HistoryInit()

        PoziAddFeature().init()
        PoziEditFeature().init()
        PoziMap().init()
        // PoziDisqus().init()
        PoziFragments().init()
        PoziGeoServer().init()

        // Example of observer model.
        /*
            var observerId1 = Model.on('app.LocalDataSourceDetected', function (value) {
              console.log('LocalDataSourceDetected changed to:)', value)
            })
            var observerId2 = Model.on('app.LocalDataSourceDetected', function (value) {
              console.log('======================== EVENTING 2 !!! :)')
            })
            if (Model.un('app.LocalDataSourceDetected', observerId2)) {
              console.log('Observer 1 unregistered.')
            }
          */

        // Redirect to https if no localDataSource configured for site.
        const PoziServerURL = Model.get('config.localDataSource.PoziServerURL', '')
        let isPoziServerURLHTTPS = false
        if (includes(PoziServerURL, 'https:')) {
          isPoziServerURLHTTPS = true
        }

        const bLocalDataSourceInConfig = Model.get('config.localDataSource', false)
        const bLocalhost = Model.get('app.localhost')
        // console.log('bLocalDataSourceInConfig', bLocalDataSourceInConfig)
        if (window.location.protocol === 'http:') {
          if (bLocalhost) {
            console.log('Skip redirect to https due to localhost.')
          } else if (bLocalDataSourceInConfig && !isPoziServerURLHTTPS) {
            console.log('Skip redirect to https due to pozi server config.')
          } else {
            window.location = window.location.href.replace('http://', 'https://')
          }
        }

        await __.loadModules()

        // Rendering the Search panel after loadModules to allow catalog layers to be loaded and checked for searchability
        const searchableLayers = PoziUtils.getLayersSearchable()
        if (searchableLayers.length > 0) {
          createRoot(document.getElementById('pozi-drawer-tabs-panel-search-react')).render(
            <Provider store={window.store}>
              <Suspense fallback={<Loading />}>
                <ThemeProvider theme={poziTheme}>
                  <SearchPanels />
                </ThemeProvider>
              </Suspense>
            </Provider>
          )
        } else {
          PoziDrawerTabSearch().hide()
        }

        $(window).resize(function () {
          $('.mdl-layout__drawer').css({
            'transform:': 'initial'
          })
          hideHotJarIfMobilePortrait()
        })

        const motdText = Model.get('config.MOTD.text', '')
        if (motdText !== '') {
          PoziNotifications().notify(motdText)
        }

        // return resolve("URL Params parsed and PoziAPI initialised.")
      })
      .catch(function (err) {
        console.error('!!! ERROR !!! Initialising error occured: ', err)
      })
  }

  __.loadModules = async function () {
    __.dataRetrieval = new PoziDataRetrieval()
    __.dataRetrieval.init()

    __.helpers = new PoziHelpers()
    __.helpers.init()
    __.helpers.checkSVGSupport()

    __.poziMap = new PoziMapBase()

    $(window).resize(__.mapDivResize)

    // Locate configuration file based on 'site' and 'app' URL params
    const siteName = Model.get('app.site')
    __.poziConfig = new PoziConfig()

    // Fetch on process the configuration
    await __.poziConfig.start(siteName)

    // Do not run in main startup thread.
    window.setTimeout(function () {
      __.pingdom = new PoziPingdom()
      __.pingdom.run()
    }, 2000)

    AuthAWS().init()
  }

  __.runTests = function () {
    if (window.debug.enabled) {
      // console.log('App: Run unit tests...')
      // PoziDataSnippet().test()
    }
  }

  __.getSiteName = function () {
    return Model.get('app.site', '')
  }

  // pt: [x,y]
  __.transformFrom4326 = function (pt) {
    if (!has(pt, 'length')) {
      console.error('Invalid point type', pt)
      return null
    }
    return OLProjTransform(pt, 'EPSG:4326', 'EPSG:3857')
  }

  __.transformTo4326 = function (pt) {
    if (!has(pt, 'length')) {
      console.error('Invalid point type', pt)
      return null
    }
    return OLProjTransform(pt, 'EPSG:3857', 'EPSG:4326')
  }

  // Manage mode visibility - an incorrect mode will hide them all
  __.activateMode = function (modeID) {
    console.log('---------------')
    console.log('Activate Mode:', modeID)

    console.log('Only show tabs corresponding to the parameter mode')
    $.each($('#elt-tabs').children(), function (idx, val) {
      if (val.id !== modeID) {
        $(val).addClass('hidden')
      } else {
        $(val).removeClass('hidden')
      }
    })

    PoziMap().resize()
    // __.mapDivResize()

    console.log('Opening up first list, if no list is currently open')
    const bListOpen = $('#' + modeID + ' .leaftab.in').length
    if (bListOpen) {
      console.log('Skipping open list one is currently open.')
    } else {
      console.log('No list is currently open, trigger first list to open...')
      const listToOpen = $('#' + modeID + ' .leaftab:first')
      listToOpen.collapse('show')
    }

    if (window.appObject.isDefaultMode()) {
      console.log('In default mode, refresh the currently open tabs.')
      $.each($('#' + modeID + ' .collapse.leaftab.in'), function (idx, val) {
        $(val).trigger('show.bs.collapse')
      })
    } else {
      console.log('Not default mode, skip tab refresh.')
    }

    if (modeID !== 'DUMMY') {
      window.store.dispatch(resetCurrentTarget())

      PoziDrawerTabInfo().displayDataMode()
      Model.set('app.drawer.activetabid', 'info')
    } else {
      PoziDrawerTabInfo().tabsMode()
    }
  }

  __.isDefaultMode = function () {
    return !$('#default').hasClass('hidden')
  }

  // Dynamically resize the different element
  // Alternative to using media queries (which didn't work)
  __.mapDivResize = function () {
    // Larger display: tabs floating on the right side
    if ($(window).width() > 767) {
      desktopDisplayMode()
    } else {
      mobileDisplayMode()
    }
  }

  // =======================================================================================================
  // Private Functions
  // =======================================================================================================

  var showApp = function (transitionTime = 1000) {
    $('#pozi-app').fadeIn(transitionTime)
  }

  var mobileDisplayMode = function () {
    // Overall body overflow: visible
    $('body').css('overflow-y', 'visible').css('overflow-x', 'hidden')
    // 300 is an arbitrary height, based on not filling in the full screen (we need to see the tabs)
    // var h_max = 300

    const h = $(window).height()
    $('#map').height(h + 'px')
    $('#elt-tabs-mode').removeClass('elt-tabs-desktop-mode').addClass('elt-tabs-mobile-mode')
    $('#elt-tabs-mode').children().removeClass('elt-tabs-children-desktop-mode').addClass('elt-tabs-children-mobile-mode')

    /* if (app.map && app.map.updateSize) {
      app.map.updateSize()
    } */
  }

  var desktopDisplayMode = function () {
    // Overall body overflow: hidden
    $('body').css('overflow', 'hidden')
    // Total combined size of the header (content + padding)
    $('#map').height($(window).height())
    // Internal scroll of tabs within the accordion
    $('#elt-tabs-mode').removeClass('elt-tabs-mobile-mode').addClass('elt-tabs-desktop-mode')
    $('#elt-tabs-mode').children().removeClass('elt-tabs-children-mobile-mode').addClass('elt-tabs-children-desktop-mode')
    /* if (app.map && app.map.updateSize) {
      app.map.updateSize()
    } */
  }
}

export default PoziApp
